import { prettifyAttoAlphAmount, prettifyTokenAmount, web3 } from "@alephium/web3";
import { useWallet } from "@alephium/web3-react";
import { useEffect, useState } from "react";
import { Leaderboard, LeaderboardTypes } from "src/artifacts/ts";
import { LeaderboardEntry } from "src/artifacts/ts/types";
import { formatAddress, formatTimeAgo } from "src/components/utils/Formatters";
import { getContractConfig } from "src/services/utils";

export default function RocketRangeLeaderboard() {
    web3.setCurrentNodeProvider(process.env.REACT_APP_NODE_URL ? process.env.REACT_APP_NODE_URL : "https://wallet.mainnet.alephium.org");
    const { connectionStatus, nodeProvider, account, signer } = useWallet()

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [leaderboard, setLeaderboard] = useState<LeaderboardTypes.State>();
    const [userScore, setUserScore] = useState<LeaderboardEntry>();

    const fetchData = async (address: string | undefined) => {
        try {
            const leaderboardRes = await Leaderboard.at(getContractConfig("Leaderboard_RocketRangeLeaderboard").address).fetchState();
            setLeaderboard(leaderboardRes);
            if (address) {
                const userLRes = await Leaderboard.at(getContractConfig("Leaderboard_RocketRangeLeaderboard").address).view.loadLeaderboardEntry({ args: { user: address } })
                setUserScore(userLRes.returns[1]);
            } else {
                setUserScore(undefined);
            }
            setError("");
        } catch (e) {
            setError("Error loading data.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setLoading(true);
        fetchData(account?.address);
    }, [account]);

    if (loading) return (<p className="text-gray-400">Loading...</p>);
    if (error) return (<p className="text-red-400">{error}</p>);
    return (
        <div className="p-6 rounded-lg shadow-lg text-white">
            {/* Header Section */}
            <div className="mb-3 text-center">
                <h2 className="text-2xl font-bold mb-1">Leaderboard</h2>
                <div className="text-sm md:text-base">
                    <p>
                        <strong>From:</strong>{" "}
                        {new Date(Number(leaderboard?.fields.currentTop.startTime)).toLocaleString()}
                    </p>
                    <p>
                        <strong>To:</strong>{" "}
                        {new Date(Number(leaderboard?.fields.currentTop.endTime)).toLocaleString()}
                    </p>
                    <p>
                        <strong>Reward Pool:</strong>{" "}
                        {prettifyAttoAlphAmount(leaderboard?.fields.currentTop.rewardPool ?? 0n)} ALPH
                    </p>
                </div>
            </div>

            {/* Card Layout */}
            <div className="grid grid-cols-1 gap-4">
                {/* First Place */}
                <div className="bg-gray-900 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-bold text-red-700">1st Place</h3>
                    <p className="text-sm text-gray-300 truncate">
                        <strong>Address:</strong> {formatAddress(leaderboard?.fields.currentTop.first.userAddr ?? "", undefined)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Weekly Score:</strong>{" "}
                        {prettifyTokenAmount(leaderboard?.fields.currentTop.first.periodicScore ?? 0n, 8)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Last Play:</strong>{" "}
                        {leaderboard?.fields.currentTop.first.lastUpdateTime == 0n ? "Never" : formatTimeAgo(Number(leaderboard?.fields.currentTop.first.lastUpdateTime))}
                    </p>
                </div>

                {/* Second Place */}
                <div className="bg-gray-900 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-bold text-red-600">2nd Place</h3>
                    <p className="text-sm text-gray-300 truncate">
                        <strong>Address:</strong> {formatAddress(leaderboard?.fields.currentTop.second.userAddr ?? "", undefined)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Weekly Score:</strong>{" "}
                        {prettifyTokenAmount(leaderboard?.fields.currentTop.second.periodicScore ?? 0n, 8)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Last Play:</strong>{" "}
                        {leaderboard?.fields.currentTop.second.lastUpdateTime == 0n ? "Never" : formatTimeAgo(Number(leaderboard?.fields.currentTop.second.lastUpdateTime))}
                    </p>
                </div>

                {/* Third Place */}
                <div className="bg-gray-900 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-bold text-red-500">3rd Place</h3>
                    <p className="text-sm text-gray-300 truncate">
                        <strong>Address:</strong> {formatAddress(leaderboard?.fields.currentTop.third.userAddr ?? "", undefined)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Weekly Score:</strong>{" "}
                        {prettifyTokenAmount(leaderboard?.fields.currentTop.third.periodicScore ?? 0n, 8)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Last Play:</strong>{" "}
                        {leaderboard?.fields.currentTop.third.lastUpdateTime == 0n ? "Never" : formatTimeAgo(Number(leaderboard?.fields.currentTop.third.lastUpdateTime))}
                    </p>
                </div>

                {/* Connected User */}
                <div className="bg-gray-900 rounded-lg p-4 shadow-md">
                    <h3 className="text-lg font-bold text-green-500">Your Stats</h3>
                    <p className="text-sm text-gray-300 truncate">
                        <strong>Address:</strong> {account ? formatAddress(account.address, undefined) : "Not Connected"}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Weekly Score:</strong>{" "}
                        {prettifyTokenAmount(userScore?.periodicScore ?? 0n, 8)}
                    </p>
                    <p className="text-sm text-gray-300">
                        <strong>Last Play:</strong>{" "}
                        {(userScore?.lastUpdateTime ?? 0n) == 0n ? "Never" : formatTimeAgo(Number(userScore?.lastUpdateTime))}
                    </p>
                </div>
            </div>
        </div>

    );
}